/**
 * Permission Accordion Matrix Component
 * Accordion-based layout for managing role permissions — replaces table-based PermissionMatrix
 *
 * Performance: Selection state is managed internally with a mutable Set + forceUpdate pattern.
 * Only the affected accordion re-renders on toggle. The parent is notified via onChange (deferred).
 */

import React, { useEffect, useState, useMemo, useCallback, useRef, useReducer } from 'react';
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Button,
  ButtonGroup,
  LinearProgress,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Tooltip,
  Link,
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  SelectAll as SelectAllIcon,
  Deselect as DeselectIcon,
  GridOn as GridOnIcon,
  ExpandMore as ExpandMoreIcon,
  Check as CheckIcon,
  UnfoldMore as UnfoldMoreIcon,
  UnfoldLess as UnfoldLessIcon,
} from '@mui/icons-material';
import { Permission, PermissionsByModule } from '../../types/rbac.types';
import { getPermissionsByModule } from '../../services/permissionService';
import {
  buildPermissionMatrix,
  splitActionColumns,
  getActionLabel,
  getActionColor,
  PermissionMatrixData,
  MatrixGroup,
} from './permissionMatrixUtils';

interface PermissionAccordionMatrixProps {
  selectedPermissionIds: number[];
  onChange: (permissionIds: number[]) => void;
  disabled?: boolean;
}

// --- Permission Tile (lightweight, no Tooltip wrapper for perf) ---

interface PermissionTileProps {
  permId: number;
  displayName: string;
  description?: string;
  action: string;
  active: boolean;
  disabled: boolean;
  isSpecial: boolean;
  onToggle: (id: number) => void;
}

const PermissionTile = React.memo<PermissionTileProps>(({
  permId,
  displayName,
  description,
  action,
  active,
  disabled,
  isSpecial,
  onToggle,
}) => {
  const color = getActionColor(action);
  const label = getActionLabel(action);

  return (
    <Tooltip title={`${displayName}${description ? ` — ${description}` : ''}`}>
      <Chip
        label={label}
        icon={active ? <CheckIcon fontSize="small" /> : undefined}
        variant={active ? 'filled' : 'outlined'}
        color={active ? color : 'default'}
        onClick={disabled ? undefined : () => onToggle(permId)}
        sx={{
          cursor: disabled ? 'default' : 'pointer',
          fontWeight: 500,
          ...(isSpecial && !active && { borderStyle: 'dashed' }),
          ...(!active && { opacity: 0.65, '&:hover': { opacity: 1 } }),
        }}
      />
    </Tooltip>
  );
});

PermissionTile.displayName = 'PermissionTile';

// --- Module Accordion ---

interface ModuleAccordionProps {
  group: MatrixGroup;
  standardColumns: string[];
  /** Bitmask string representing which group permissions are selected (e.g. "1100") */
  selectionKey: string;
  disabled: boolean;
  expanded: boolean;
  onToggleExpand: (groupName: string) => void;
  onTogglePermission: (id: number) => void;
  onToggleAllInGroup: (group: MatrixGroup) => void;
}

const ModuleAccordion = React.memo<ModuleAccordionProps>(({
  group,
  standardColumns,
  selectionKey,
  disabled,
  expanded,
  onToggleExpand,
  onTogglePermission,
  onToggleAllInGroup,
}) => {
  const totalCount = group.permissions.length;
  let selectedCount = 0;
  for (let i = 0; i < selectionKey.length; i++) {
    if (selectionKey[i] === '1') selectedCount++;
  }
  const allSelected = selectedCount === totalCount && totalCount > 0;

  // Classify permissions into standard CRUD vs special
  const standardSet = useMemo(() => new Set(standardColumns), [standardColumns]);

  return (
    <Accordion
      expanded={expanded}
      onChange={() => onToggleExpand(group.groupName)}
      disableGutters
      slotProps={{ transition: { unmountOnExit: true } }}
      sx={{
        '&:before': { display: 'none' },
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px !important',
        mb: 1,
        overflow: 'hidden',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          backgroundColor: selectedCount > 0 ? 'action.hover' : 'transparent',
          '& .MuiAccordionSummary-content': {
            alignItems: 'center',
            gap: 1.5,
            my: 1,
          },
        }}
      >
        <Typography variant="subtitle1" fontWeight={600} sx={{ flexGrow: 1 }}>
          {group.groupName}
        </Typography>
        <Chip
          label={`${selectedCount}/${totalCount}`}
          size="small"
          color={allSelected ? 'success' : selectedCount > 0 ? 'warning' : 'default'}
          variant={selectedCount > 0 ? 'filled' : 'outlined'}
          sx={{ fontWeight: 600, minWidth: 50 }}
        />
        <Switch
          size="small"
          checked={allSelected}
          disabled={disabled}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            e.stopPropagation();
            onToggleAllInGroup(group);
          }}
          sx={{ ml: 0.5 }}
        />
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 2, pb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          {(() => {
            const standardPerms: Permission[] = [];
            const specialPerms: Permission[] = [];
            for (const perm of group.permissions) {
              if (standardSet.has(perm.action)) {
                standardPerms.push(perm);
              } else {
                specialPerms.push(perm);
              }
            }
            const permIndex = new Map<number, number>();
            group.permissions.forEach((p, i) => permIndex.set(p.id, i));

            return (
              <>
                {standardPerms.map((perm) => (
                  <PermissionTile
                    key={perm.id}
                    permId={perm.id}
                    displayName={perm.displayName}
                    description={perm.description}
                    action={perm.action}
                    active={selectionKey[permIndex.get(perm.id)!] === '1'}
                    disabled={disabled}
                    isSpecial={false}
                    onToggle={onTogglePermission}
                  />
                ))}
                {standardPerms.length > 0 && specialPerms.length > 0 && (
                  <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                )}
                {specialPerms.map((perm) => (
                  <PermissionTile
                    key={perm.id}
                    permId={perm.id}
                    displayName={perm.displayName}
                    description={perm.description}
                    action={perm.action}
                    active={selectionKey[permIndex.get(perm.id)!] === '1'}
                    disabled={disabled}
                    isSpecial={true}
                    onToggle={onTogglePermission}
                  />
                ))}
              </>
            );
          })()}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
});

ModuleAccordion.displayName = 'ModuleAccordion';

// --- Main Component ---

const PermissionAccordionMatrix: React.FC<PermissionAccordionMatrixProps> = ({
  selectedPermissionIds,
  onChange,
  disabled = false,
}) => {
  const [permissionsByModule, setPermissionsByModule] = useState<PermissionsByModule>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Internal selection state — a mutable Set for O(1) toggle + a version counter to trigger re-renders
  const selectionRef = useRef(new Set<number>(selectedPermissionIds));
  const [, forceRender] = useReducer((x: number) => x + 1, 0);

  // Stable onChange ref
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Sync from parent when selectedPermissionIds changes externally (e.g., role switch)
  const prevExternalIds = useRef(selectedPermissionIds);
  if (prevExternalIds.current !== selectedPermissionIds) {
    prevExternalIds.current = selectedPermissionIds;
    selectionRef.current = new Set(selectedPermissionIds);
  }

  useEffect(() => {
    loadPermissions();
  }, []);

  const loadPermissions = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const response = await getPermissionsByModule({ isActive: true });
      setPermissionsByModule(response.data);
    } catch (err: unknown) {
      console.error('Error loading permissions:', err);
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Erro ao carregar permissoes');
    } finally {
      setLoading(false);
    }
  };

  // Build matrix data
  const matrixData: PermissionMatrixData = useMemo(
    () => buildPermissionMatrix(permissionsByModule),
    [permissionsByModule],
  );

  const { standardColumns } = useMemo(
    () => splitActionColumns(matrixData.actionColumns),
    [matrixData.actionColumns],
  );

  // Compute per-group selection bitmask strings
  const groupSelectionKeys = useMemo(() => {
    const sel = selectionRef.current;
    const keys: Record<string, string> = {};
    for (const group of matrixData.groups) {
      let key = '';
      for (const perm of group.permissions) {
        key += sel.has(perm.id) ? '1' : '0';
      }
      keys[group.groupName] = key;
    }
    return keys;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matrixData.groups, selectedPermissionIds]);

  // Filtered groups by search
  const filteredGroups = useMemo(() => {
    if (!searchTerm.trim()) return matrixData.groups;
    const lower = searchTerm.toLowerCase();
    return matrixData.groups.filter((g) => g.groupName.toLowerCase().includes(lower));
  }, [matrixData.groups, searchTerm]);

  // Stats
  const selectedCount = selectionRef.current.size;
  const totalCount = matrixData.totalPermissions;
  const percentage = totalCount > 0 ? Math.round((selectedCount / totalCount) * 100) : 0;

  // --- Notify parent ---
  const notifyParent = useCallback(() => {
    onChangeRef.current(Array.from(selectionRef.current));
  }, []);

  // --- Expand/Collapse ---

  const handleToggleExpand = useCallback((groupName: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });
  }, []);

  const handleExpandAll = useCallback(() => {
    setExpandedGroups(new Set(filteredGroups.map((g) => g.groupName)));
  }, [filteredGroups]);

  const handleCollapseAll = useCallback(() => {
    setExpandedGroups(new Set());
  }, []);

  // --- Permission toggle (mutate Set directly, force re-render, notify parent) ---

  const handleTogglePermission = useCallback(
    (permissionId: number) => {
      if (disabled) return;
      const sel = selectionRef.current;
      if (sel.has(permissionId)) {
        sel.delete(permissionId);
      } else {
        sel.add(permissionId);
      }
      forceRender();
      notifyParent();
    },
    [disabled, notifyParent],
  );

  const handleToggleAllInGroup = useCallback(
    (group: MatrixGroup) => {
      if (disabled) return;
      const sel = selectionRef.current;
      const groupIds = group.permissions.map((p) => p.id);
      const allSelected = groupIds.every((id) => sel.has(id));

      if (allSelected) {
        for (const id of groupIds) sel.delete(id);
      } else {
        for (const id of groupIds) sel.add(id);
      }
      forceRender();
      notifyParent();
    },
    [disabled, notifyParent],
  );

  // --- Presets ---

  const applyPreset = useCallback((idsFn: () => number[]) => {
    if (disabled) return;
    selectionRef.current = new Set(idsFn());
    forceRender();
    notifyParent();
  }, [disabled, notifyParent]);

  const handlePresetReadOnly = useCallback(() => {
    applyPreset(() => {
      const ids: number[] = [];
      for (const group of matrixData.groups) {
        for (const perm of group.permissions) {
          if (perm.action === 'read') ids.push(perm.id);
        }
      }
      return ids;
    });
  }, [applyPreset, matrixData.groups]);

  const handlePresetCrud = useCallback(() => {
    applyPreset(() => {
      const crudActions = new Set(['read', 'create', 'update', 'delete']);
      const ids: number[] = [];
      for (const group of matrixData.groups) {
        for (const perm of group.permissions) {
          if (crudActions.has(perm.action)) ids.push(perm.id);
        }
      }
      return ids;
    });
  }, [applyPreset, matrixData.groups]);

  const handlePresetAll = useCallback(() => {
    applyPreset(() => {
      const ids: number[] = [];
      for (const group of matrixData.groups) {
        for (const perm of group.permissions) {
          ids.push(perm.id);
        }
      }
      return ids;
    });
  }, [applyPreset, matrixData.groups]);

  const handlePresetClear = useCallback(() => {
    applyPreset(() => []);
  }, [applyPreset]);

  // --- Render ---

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      {/* Search */}
      <TextField
        fullWidth
        placeholder="Buscar modulo..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        size="small"
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      {/* Progress bar */}
      <Box sx={{ mb: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
          <Typography variant="body2" color="text.secondary">
            {selectedCount} de {totalCount} selecionadas
          </Typography>
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            {percentage}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={percentage}
          sx={{ height: 8, borderRadius: 4 }}
        />
      </Box>

      {/* Presets + Expand/Collapse */}
      <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'space-between' }}>
        <ButtonGroup size="small" variant="outlined" disabled={disabled}>
          <Button startIcon={<VisibilityIcon />} onClick={handlePresetReadOnly}>
            Somente Leitura
          </Button>
          <Button startIcon={<GridOnIcon />} onClick={handlePresetCrud}>
            CRUD Completo
          </Button>
          <Button startIcon={<SelectAllIcon />} onClick={handlePresetAll}>
            Acesso Total
          </Button>
          <Button startIcon={<DeselectIcon />} onClick={handlePresetClear} color="error">
            Limpar Tudo
          </Button>
        </ButtonGroup>

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Link
            component="button"
            variant="body2"
            underline="hover"
            onClick={handleExpandAll}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <UnfoldMoreIcon fontSize="small" />
            Expandir todos
          </Link>
          <Link
            component="button"
            variant="body2"
            underline="hover"
            onClick={handleCollapseAll}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <UnfoldLessIcon fontSize="small" />
            Recolher todos
          </Link>
        </Box>
      </Box>

      {/* Accordion List */}
      <Box>
        {filteredGroups.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {searchTerm ? 'Nenhum modulo encontrado' : 'Nenhuma permissao disponivel'}
            </Typography>
          </Box>
        ) : (
          filteredGroups.map((group) => (
            <ModuleAccordion
              key={group.groupName}
              group={group}
              standardColumns={standardColumns}
              selectionKey={groupSelectionKeys[group.groupName] ?? ''}
              disabled={disabled}
              expanded={expandedGroups.has(group.groupName)}
              onToggleExpand={handleToggleExpand}
              onTogglePermission={handleTogglePermission}
              onToggleAllInGroup={handleToggleAllInGroup}
            />
          ))
        )}
      </Box>
    </Box>
  );
};

export default PermissionAccordionMatrix;
